import React, { useRef, useState } from 'react';
import { ShieldCheck, Loader2, Upload, AlertTriangle } from 'lucide-react';
import { uploadIdProof } from '../services/api';
import { toast } from 'sonner';

const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
const MAX = 5 * 1024 * 1024; // 5 MB

export const UploadIdProof = ({ uploaded, required, onUploaded, compact = false }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error('Please upload a JPG, PNG, WEBP or PDF file');
      return;
    }
    if (f.size > MAX) {
      toast.error('File must be 5 MB or smaller');
      return;
    }
    setBusy(true);
    try {
      await uploadIdProof(f);
      toast.success('ID proof uploaded successfully');
      onUploaded && onUploaded();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload failed';
      toast.error(detail);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const ok = !!uploaded;
  const hardBlock = required && !ok;

  return (
    <div
      className={`rounded-2xl border ${hardBlock ? 'border-red-200 bg-red-50' : ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'} p-4 ${compact ? '' : 'sm:p-5'}`}
      data-testid="id-proof-card"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${hardBlock ? 'bg-red-100 text-red-600' : ok ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
          {hardBlock ? <AlertTriangle className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 text-sm">
            {ok ? 'ID Proof on File' : (hardBlock ? 'ID Proof Required' : 'Upload Your ID Proof')}
          </h4>
          <p className="text-xs text-gray-600 mt-0.5">
            {ok
              ? 'Thanks! Your document has been received and is auto-accepted. Admin may review it later.'
              : hardBlock
                ? 'Please upload a valid government ID (Aadhaar / PAN / Driving Licence) to continue.'
                : 'Upload one valid government ID — Aadhaar, PAN or Driving Licence — for verification.'}
          </p>
          {!ok && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className={`mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${hardBlock ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'} disabled:opacity-50`}
              data-testid="id-proof-upload-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {busy ? 'Uploading…' : 'Upload ID Proof'}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onPick} data-testid="id-proof-file-input" />
        </div>
      </div>
    </div>
  );
};

export default UploadIdProof;
